import React from "react";
import { PRODUCTS, INDUSTRIES } from "../../data";
import { SectionLabel, SectionTitle, SectionSub } from "../ui/Typography";
import useScrollFade from "../../hooks/useScrollFade";

/* ── Marquee ─────────────────────────────────────── */
const ITEMS = [
  "Trusted by Chase Value",
  "AI & Machine Learning",
  "Computer Vision Analytics",
  "Enterprise ERP Integration",
  "Bacha Party Partner",
  "Retail Intelligence",
  "Predictive Analytics",
  "Auto-Replenishment AI",
];

export const MarqueeBar = () => (
  <div className="bg-slate-50 border-y border-slate-200 py-4 overflow-hidden">
    <div className="flex gap-14 animate-marquee whitespace-nowrap select-none">
      {/* duplicate for seamless loop */}
      {[...ITEMS, ...ITEMS].map((item, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-2 font-head font-semibold text-slate-500 text-sm shrink-0"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block shrink-0" />
          {item}
        </span>
      ))}
    </div>
  </div>
);

/* ── Single product card ─────────────────────────── */
const ProductCard = ({ product, navigate, delay = 0 }) => {
  const ref = useScrollFade();
  return (
    <div
      ref={ref}
      className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl p-7 relative overflow-hidden group cursor-pointer"
      style={{ transitionDelay: `${delay}ms` }}
      onClick={() => navigate("products")}
    >
      {/* Top accent line */}
      <span className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${product.iconBg} flex items-center justify-center text-2xl mb-5`}>
        {product.emoji}
      </div>

      <span className={`inline-block text-xs font-head font-semibold px-2.5 py-1 rounded-full border mb-3 ${product.tagClass}`}>
        {product.tag}
      </span>

      <h3 className="font-head font-bold text-slate-900 text-lg mb-2">{product.name}</h3>
      <p className="text-slate-500 text-sm leading-relaxed mb-5 line-clamp-3">{product.desc}</p>

      <span className="inline-flex items-center gap-1.5 text-blue-600 font-head font-semibold text-sm group-hover:gap-3 transition-all">
        Explore Product
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
        </svg>
      </span>
    </div>
  );
};

/* ── Products grid section ───────────────────────── */
export const ProductsGridSection = ({ navigate }) => {
  const titleRef = useScrollFade();
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={titleRef} className="fade-in-section text-center mb-12">
          <SectionLabel>Our Flagship Products</SectionLabel>
          <SectionTitle>
            Intelligent Software{" "}
            <span className="text-blue-600">Built for Scale</span>
          </SectionTitle>
          <SectionSub center>
            Purpose-built AI products for retail, enterprise, and field operations — proven in production.
          </SectionSub>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} navigate={navigate} delay={i * 80} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── Industries section ──────────────────────────── */
export const IndustriesSection = () => {
  const titleRef = useScrollFade();
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={titleRef} className="fade-in-section text-center mb-12">
          <SectionLabel>Industries We Serve</SectionLabel>
          <SectionTitle>
            Across <span className="text-blue-600">Every Sector</span>
          </SectionTitle>
          <SectionSub center>Our AI solutions are built for the real world — tailored to the unique challenges of each industry.</SectionSub>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {INDUSTRIES.map((ind) => (
            <div
              key={ind.name}
              className="card-hover bg-white border-2 border-slate-200 rounded-xl p-4 text-center hover:border-blue-500 hover:bg-blue-50/40 cursor-default group"
              title={ind.detail}
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">{ind.icon}</div>
              <p className="font-head font-semibold text-slate-700 text-xs leading-tight">{ind.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
